import {
  PListDto,
  PListDetailsDto,
  PListLineMemo,
  PListLineMemoConfig,
  OrderSSCCDto,
  CreateOrderSSCCRequest,
  UpdateOrderSSCCRequest,
  PalletSummaryDto,
  OrderSerialNoDto,
  CreateOrderSerialNoRequest,
  OrderBatchNoDto,
  CreateOrderBatchNoRequest,
  UpdateOrderBatchNoRequest,
  DeletePickingListResult,
  ClosePickingListWithoutExportResult,
  RegeneratePickingListResult,
  AdditionalItemDto,
  AddAdditionalLineRequest,
    AddAdditionalLineResponse,
    AggregatedPickingItemDto,
    SSCCValidationResult,
    ValidateOperatorBatchResponse,
    CreateOperatorBatchResponse,
    SendToPackingResult,
    TaValidationResponse,
    PackZoneLabelResponse,
    PListLockHistoryDto,
    PListLockOperatorSummaryDto,
    PListLockPeriodDto,
    HandoverPListDto,
    CountryCodeOverride
} from '../types/picking';
import { CompletedPickListDto, CompletedPickListDetailDto, CompletedPickListLineDto } from '../types/completedPickingList';
import { AuthService } from './authService';
import { throwIfLockConflict } from '../utils/lockConflictError';
import { extractApiError, createApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class PickingService {

  /**
   * Genererer en ny SSCC (pall-ID) fra firmaets SSCC-konfigurasjon (SystemSetupSSCC).
   * Returnerer null hvis SSCC ikke er konfigurert (HTTP 204) — kaller bør da falle
   * tilbake til en lokal pall-ID (f.eks. `PALL-${Date.now()}`).
   */
  async generateSscc(): Promise<string | null> {
    try {
      const url = `${API_BASE_URL}/picking/sscc/generate`;
      const response = await AuthService.authenticatedPost(url, {});

      if (response.status === 204) {
        return null; // Ikke konfigurert
      }
      if (!response.ok) {
        console.warn(`generateSscc failed: ${response.status} ${response.statusText}`);
        return null;
      }

      const data = await response.json();
      return data?.sscc || null;
    } catch (error) {
      console.warn('generateSscc error, falling back to local pallet id:', error);
      return null;
    }
  }

  // OrderSSCC (Pallet Management) Methods
  async getPallets(pListId: number): Promise<PalletSummaryDto[]> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/pallets`;
    const response = await AuthService.authenticatedGet(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch pallets: ${response.statusText}`);
    }
    
    return response.json();
  }

  async getOrderSSCCs(pListId: number): Promise<OrderSSCCDto[]> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/sscc`;
    const response = await AuthService.authenticatedGet(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch SSCC records: ${response.statusText}`);
    }
    
    return response.json();
  }

  async createOrUpdateSSCC(pListId: number, request: CreateOrderSSCCRequest): Promise<OrderSSCCDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/sscc`;
    const response = await AuthService.authenticatedPost(url, request);
    
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to create/update SSCC'));
    }
    
    return response.json();
  }

  /** Bytt lastbærertype på en pall — kun før avslutning (TW 36396318). */
  async setPalletTransitPackaging(pListId: number, sscc: string, transitPackagingID: number | null): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/pallets/transit-packaging`;
    const response = await AuthService.authenticatedPut(url, { sscc, transitPackagingID });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to change carrier type'));
    }
  }

  async updateOrderSSCC(pListId: number, ssccId: number, request: UpdateOrderSSCCRequest): Promise<OrderSSCCDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/sscc/${ssccId}`;
    const response = await AuthService.authenticatedPut(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update SSCC'));
    }

    return response.json();
  }

  async deleteSSCC(pListId: number, ssccId: number): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/sscc/${ssccId}`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to delete SSCC'));
    }
  }

  /** «Avslutt uten eksport» (TW 36401597): status 60 uten ERP-eksport, reservasjoner/lås frigis. */
  async closePickingListWithoutExport(pListId: number): Promise<ClosePickingListWithoutExportResult> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/close-without-export`;
    const response = await AuthService.authenticatedPost(url, {});
    const result = await response.json();
    if (!response.ok) {
      throw new Error(extractApiError(result, response, 'Failed to close picking list without export'));
    }
    return result;
  }

  async regeneratePickingListReservations(pListId: number): Promise<RegeneratePickingListResult> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/regenerate-reservations`;
    const response = await AuthService.authenticatedPost(url, {});
    const result = await response.json();
    if (!response.ok) {
      throw new Error(extractApiError(result, response, 'Failed to regenerate picking list reservations'));
    }
    return result;
  }

  async deletePickingList(pListId: number): Promise<DeletePickingListResult> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}`;
    const response = await AuthService.authenticatedDelete(url);

    const result = await response.json();

    if (!response.ok) {
      throw new Error(extractApiError(result, response, 'Failed to delete picking list'));
    }

    return result;
  }

  async getSSCCContents(sscc: string): Promise<OrderSSCCDto[]> {
    const url = `${API_BASE_URL}/picking/sscc/${encodeURIComponent(sscc)}`;
    const response = await AuthService.authenticatedGet(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch SSCC contents: ${response.statusText}`);
    }
    
    return response.json();
  }

  async getPickingDetails(pListId: number): Promise<PListDetailsDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch picking details: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * TW 36430608: status for en plukkliste, eller null når den ikke finnes (404).
   * Brukes av oppryddingen av kladder i kollidialogen.
   */
  async getPickingListStatus(pListId: number): Promise<number | null> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}`;
    const response = await AuthService.authenticatedGet(url);
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`Failed to fetch picking list status: ${response.statusText}`);
    }
    const details: PListDetailsDto = await response.json();
    return details.header.pListStatus;
  }

  async startPicking(pListId: number, operatorId?: string): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/start`;
    const response = await AuthService.authenticatedPost(url, operatorId ? { operatorId } : undefined);

    if (!response.ok) {
      await throwIfLockConflict(response);
      const error = await response.json().catch(() => null);
      // ApiError beholder errorCode (PLIST_NOT_FOUND, PLIST_NOT_APPROVED, PLIST_ALREADY_TERMINAL, …)
      throw createApiError(error, response, 'Failed to start picking');
    }
  }

  /**
   * «Neste plukk»: backend velger høyest prioriterte ledige plukk og starter det atomisk.
   * Returnerer null når ingen ledige plukk finnes (404 NO_AVAILABLE_PICKING_LISTS).
   */
  async startNextPicking(warehouseId?: string): Promise<PListDto | null> {
    const queryString = warehouseId ? `?warehouseId=${encodeURIComponent(warehouseId)}` : '';
    const url = `${API_BASE_URL}/picking/next/start${queryString}`;
    const response = await AuthService.authenticatedPost(url, undefined);

    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw createApiError(error, response, 'Failed to start next picking');
    }

    return response.json();
  }

  async completePicking(
    pListId: number,
    operatorId?: string,
    confirmNoCarrier?: boolean,
    /** Landkoder fra TA-valideringsdialogen (36376022) — gjelder kun denne bookingen. */
    countryCodeOverrides?: CountryCodeOverride[]
  ): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/complete`;
    const hasOverrides = !!countryCodeOverrides && countryCodeOverrides.length > 0;
    const body = (operatorId || confirmNoCarrier || hasOverrides)
      ? {
          operatorId,
          confirmNoCarrier: confirmNoCarrier || undefined,
          countryCodeOverrides: hasOverrides ? countryCodeOverrides : undefined
        }
      : undefined;
    const response = await AuthService.authenticatedPost(url, body);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw createApiError(error, response, 'Failed to complete picking');
    }
  }

  async validatePackingLocation(): Promise<{ isValid: boolean; packingBinCode: string | null; errorMessage: string | null }> {
    const url = `${API_BASE_URL}/picking/validate-packing-location`;
    const response = await AuthService.authenticatedGet(url);
    if (!response.ok) {
      throw new Error('Failed to validate packing location');
    }
    return response.json();
  }

  async sendToPacking(pListId: number, operatorId?: string, packZoneID?: string): Promise<SendToPackingResult> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/to-packing`;
    // Pakkesone settes av backend (kun PackZoneID) i samme kall — TW 36391911.
    const requestBody = operatorId || packZoneID ? { operatorId, packZoneID } : undefined;
    const response = await AuthService.authenticatedPost(url, requestBody);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to send to packing'));
    }

    // P14 brødre-plukk: respons inneholder sistemann-flagg.
    const body = await response.json().catch(() => ({}));
    return {
      isLastSibling: body?.isLastSibling ?? false,
      siblingCount: body?.siblingCount ?? 0
    };
  }

  /**
   * TA-forhåndsvalidering før fullføring (TW 36369121). Gated bak innstillingen
   * ta.validateBeforeCompletion — applicable=false betyr «fortsett uten dialog».
   * Feiler åpent: nettverksfeil skal aldri blokkere avslutningen (kalleren fanger).
   */
  async validateTaBeforeCompletion(pListId: number): Promise<TaValidationResponse> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/validate-ta`;
    const response = await AuthService.authenticatedPost(url, {});
    if (!response.ok) {
      throw new Error('TA-validering utilgjengelig');
    }
    return response.json();
  }

  /**
   * Plukkliste-utskrift ved «send til pakking» (TW 36369120 + 36370081): én plukkliste
   * per plukk (dokumentmodus). Gated bak innstillingen picking.packZoneLabelReportId
   * (enabled=false = av). Kalles etter vellykket sendToPacking; brukes også til reprint.
   */
  async printPackZoneLabels(pListId: number, packZoneID?: string): Promise<PackZoneLabelResponse> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/pack-zone-labels`;
    const response = await AuthService.authenticatedPost(url, { packZoneID });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Kunne ikke skrive ut plukkliste til pakkesone'));
    }
    return response.json();
  }

  async updatePickedQuantity(
    pListId: number,
    lineNo: number,
    qtyPicked: number,
    operatorId?: string,
    startDateTime?: string,
    serialNo?: string,
    entryMethod?: 'Scanned' | 'Manual',
    scannedBarcode?: string
  ): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines`;
    const response = await AuthService.authenticatedPut(url, {
      lineNo,
      qtyPicked,
      operatorId,
      startDateTime,
      serialNo,
      entryMethod,
      scannedBarcode
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update picked quantity'));
    }
  }

  /**
   * Erstatter en dummy-serial (MIG-*) med et ekte skannet serienummer og utfører plukk.
   * Backend velger den eldste dummyen på plukk-lokasjonen automatisk.
   *
   * Validering: newSerialNo må starte med varenummer (itemNo). Hvis ikke, returnerer
   * backend `replaced=false` slik at kaller kan vise vanlig "serienummer finnes ikke"-feil.
   */
  async replaceDummyAndPickSerial(
    pListId: number,
    lineNo: number,
    newSerialNo: string,
    operatorId?: string,
    startDateTime?: string
  ): Promise<{ replaced: boolean; newSerialNo?: string; oldSerialNo?: string; message?: string }> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/replace-dummy-and-pick-serial`;
    const response = await AuthService.authenticatedPost(url, {
      lineNo,
      newSerialNo,
      operatorId,
      startDateTime
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Kunne ikke erstatte dummy-serial'));
    }

    return await response.json();
  }

  async updateBinCode(
    pListId: number,
    lineNo: number,
    binCode: string,
    operatorId?: string
  ): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${lineNo}/bincode`;
    const response = await AuthService.authenticatedPut(url, {
      binCode,
      operatorId
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update BinCode'));
    }
  }

  /**
   * Løs opp [X]-shortage på en plukklinje når operatøren visuelt har funnet
   * varen på en lokasjon Compass ikke har registrert saldo for. Backend gjør:
   * 1. Intern saldokorreksjon (ExportedYesNo=1, sendes IKKE til ERP).
   * 2. Oppdaterer PListLine.BinCode + oppretter StcReservation.
   * Etterpå kan operatør plukke normalt — vanlig plukk-eksport til ERP er uendret.
   * Kun tilgjengelig for AW-lager (Warehouse.UseAWYesNo=1).
   */
  async relocateWithDiscovery(
    pListId: number,
    lineNo: number,
    request: {
      newBinCode: string;
      discoveredQuantity: number;
      reasonCodeNo: number;
      comment?: string;
    }
  ): Promise<{
    success: boolean;
    previousBinCode: string | null;
    newBinCode: string;
    correctionBatchId: string | null;
    correctionDelta: number;
  }> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${lineNo}/relocate-with-discovery`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Shortage-resolution feilet'));
    }

    return await response.json();
  }

  /**
   * «Bekreft tomt» (Ariens): meld at en plukkbar linje er fysisk tom. Backend konverterer
   * linjen til manko ([X]) og frigjør reservasjonen. ItemBins-saldo røres ikke.
   */
  async reportLineMissing(
    pListId: number,
    lineNo: number
  ): Promise<{ success: boolean; pListId: number; lineNo: number; releasedReservationId: number }> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${lineNo}/report-missing`;
    const response = await AuthService.authenticatedPost(url, {});

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Kunne ikke melde linje tom'));
    }

    return await response.json();
  }


  async getAvailablePickingLists(operatorId?: string, warehouseId?: string): Promise<PListDto[]> {
    const params = new URLSearchParams();
    if (operatorId) params.append('operatorId', operatorId);
    if (warehouseId) params.append('warehouseId', warehouseId);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const url = `${API_BASE_URL}/picking/available${queryString}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch available picking lists: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Utlevering (36334240): søk plukklister på ordrenivå (ERP-ordrenr/kundenavn)
   * for utleveringsbildet. Grupperes per ordre i komponenten.
   */
  async searchHandoverPLists(searchTerm: string, warehouseId?: string): Promise<HandoverPListDto[]> {
    const params = new URLSearchParams({ searchTerm });
    if (warehouseId) params.append('warehouseId', warehouseId);
    const url = `${API_BASE_URL}/picking/handover-search?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to search handover picking lists: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Utlevering standardliste (TW 36403720): usignerte, ferdigplukkede plukk for leveringsmåter
   * merket «Utlevering ved disk», siste 14 dager. Vises før operatøren søker.
   */
  async getHandoverDefaultPLists(days = 14, warehouseId?: string): Promise<HandoverPListDto[]> {
    const params = new URLSearchParams({ days: String(days) });
    if (warehouseId) params.append('warehouseId', warehouseId);
    const url = `${API_BASE_URL}/picking/handover-default?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to load handover default picking lists: ${response.statusText}`);
    }

    return response.json();
  }

  async lockPickingList(pListId: number, operatorId?: string): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lock`;
    const response = await AuthService.authenticatedPost(url, operatorId ? { operatorId } : undefined);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      // ApiError beholder errorCode (PLIST_SENT_TO_PACKING, PLIST_STATUS_TOO_HIGH, …)
      throw createApiError(error, response, 'Failed to lock picking list');
    }
  }

  async unlockPickingList(pListId: number, operatorId?: string): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/unlock`;
    const response = await AuthService.authenticatedPost(url, operatorId ? { operatorId } : undefined);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to unlock picking list'));
    }
  }

  // Ta-og-slipp-sporing (Teamwork 36333980): låsehistorikk for lagersjef
  async getLockHistory(days: number = 30, operatorId?: string, pListId?: number, limit: number = 200): Promise<PListLockHistoryDto[]> {
    const params = new URLSearchParams({ days: days.toString(), limit: limit.toString() });
    if (operatorId) params.append('operatorId', operatorId);
    if (pListId) params.append('pListId', pListId.toString());
    const url = `${API_BASE_URL}/picking/lock-history?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch lock history: ${response.statusText}`);
    }

    return await response.json();
  }

  // TW 36396507: én rad per lås (tatt → sluppet/tvang/omfordelt/holdes nå)
  async getLockPeriods(days: number = 30, operatorId?: string, limit: number = 300): Promise<PListLockPeriodDto[]> {
    const params = new URLSearchParams({ days: days.toString(), limit: limit.toString() });
    if (operatorId) params.append('operatorId', operatorId);
    const url = `${API_BASE_URL}/picking/lock-history/periods?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch lock periods: ${response.statusText}`);
    }

    return await response.json();
  }

  async getLockHistorySummary(days: number = 30): Promise<PListLockOperatorSummaryDto[]> {
    const url = `${API_BASE_URL}/picking/lock-history/summary?days=${days}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch lock history summary: ${response.statusText}`);
    }

    return await response.json();
  }

  async getPickingListsByStatus(status: number, warehouseId?: string): Promise<PListDto[]> {
    const params = new URLSearchParams();
    params.append('status', status.toString());
    if (warehouseId) params.append('warehouseId', warehouseId);
    const url = `${API_BASE_URL}/picking/lists/by-status?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch picking lists by status: ${response.statusText}`);
    }

    return response.json();
  }

  async getPickingListsByStatuses(statuses: number[], warehouseId?: string): Promise<PListDto[]> {
    const params = new URLSearchParams();
    statuses.forEach(s => params.append('statuses', s.toString()));
    if (warehouseId) params.append('warehouseId', warehouseId);
    const url = `${API_BASE_URL}/picking/lists/by-statuses?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch picking lists by statuses: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get picking list history (status 60 - completed)
   * @param operatorId Optional operator ID filter. If null, filters to current operator
   * @param dateFrom Optional start date filter
   * @param dateTo Optional end date filter
   * @param allOperators If true, returns history for all operators (for warehouse managers)
   */
  async getPickingListHistory(
    operatorId?: string,
    dateFrom?: Date,
    dateTo?: Date,
    allOperators: boolean = false,
    warehouseId?: string
  ): Promise<PListDto[]> {
    const params = new URLSearchParams();

    if (operatorId) {
      params.append('operatorId', operatorId);
    }
    if (dateFrom) {
      params.append('dateFrom', dateFrom.toISOString());
    }
    if (dateTo) {
      params.append('dateTo', dateTo.toISOString());
    }
    if (allOperators) {
      params.append('allOperators', 'true');
    }
    if (warehouseId) {
      params.append('warehouseId', warehouseId);
    }

    const url = `${API_BASE_URL}/picking/history${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch picking list history: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Format date for display
   */
  formatDate(dateString?: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('no-NO');
  }

  /**
   * Format date and time for display
   */
  formatDateTime(dateString?: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('no-NO');
  }

  /**
   * Get picking list status text
   */
  getStatusText(status: number): string {
    switch (status) {
      case 0: return 'Ubehandlet';
      case 10: return 'Godkjent';
      case 20: return 'Under arbeid';
      case 30: return 'Behandlet ikke komplett';
      case 40: return 'Behandlet komplett';
      case 45: return 'Til pakking';
      case 50: return 'Klar for eksport';
      case 60: return 'Eksportert';
      default: return `Status ${status}`;
    }
  }

  /**
   * Update a picking list line
   */
  async updatePListLine(pListId: number, lineNo: number, line: any): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${lineNo}`;
    const response = await AuthService.authenticatedPut(url, line);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update picking list line'));
    }
  }

  /**
   * Update the sort order of picking list lines
   */
  async updateLineOrder(
    pListId: number,
    lineOrders: Array<{ pListLineNo: number; sortOrder: number }>
  ): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/reorder`;
    const response = await AuthService.authenticatedPut(url, lineOrders);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update line order'));
    }
  }

  /**
   * Update a picking list header (PList).
   * Returnerer ny LastUpdate (samtidighets-token) slik at kalleren kan oppdatere sitt snapshot og
   * unngå at egne påfølgende redigeringer av samme rad avvises som "endret av andre".
   */
  /**
   * Returnerer også lagret lås og ev. varsel: på plukk sendt til vareheis beholder backend
   * Låst av/Systemstatus (vareheis-vakten) og lagrer resten — da kommer `warning` med.
   */
  async updatePickingList(pListId: number, pickingList: any): Promise<{
    lastUpdate: string | null;
    lockStatus?: number;
    lockedBy?: string | null;
    warning?: string | null;
  }> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}`;
    const response = await AuthService.authenticatedPut(url, pickingList);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw createApiError(error, response, 'Failed to update picking list');
    }

    const data = await response.json().catch(() => null);
    return {
      lastUpdate: data?.lastUpdate ?? null,
      lockStatus: data?.lockStatus,
      lockedBy: data?.lockedBy,
      warning: data?.warning ?? null
    };
  }

  /**
   * Sett leveringsmåte (DeliveryMethodNo) på alle ordrer som hører til ett plukk.
   * Manuell overstyring fra bulk-modalen — skriver kun til PListOrders (fasit for
   * Leveringsmåte-visning + TA-eksport i plukk-flyt), ikke Orders.DeliveryMethodNo.
   */
  async updatePickingListDeliveryMethod(pListId: number, deliveryMethodNo: number): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/delivery-method`;
    const response = await AuthService.authenticatedPut(url, { deliveryMethodNo });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update delivery method'));
    }
  }

  // ===== New methods for Completed Picking Lists (Avsluttede plukklister) =====

  /**
   * Get completed pick lists (status 60) with enhanced information.
   * Includes calculated delivery dates and aggregated signature flags.
   * Default date range: last 7 days.
   *
   * @param dateFrom Optional start date filter (defaults to 7 days ago)
   * @param dateTo Optional end date filter (defaults to today)
   * @returns List of completed pick lists with enhanced information
   */
  async getCompletedPickLists(
    dateFrom?: Date,
    dateTo?: Date,
    warehouseId?: string
  ): Promise<CompletedPickListDto[]> {
    // Default to last 7 days if no date range provided
    const defaultDateFrom = new Date();
    defaultDateFrom.setDate(defaultDateFrom.getDate() - 7);

    const fromDate = dateFrom || defaultDateFrom;
    const toDate = dateTo || new Date();

    const params = new URLSearchParams();
    params.append('dateFrom', fromDate.toISOString());
    params.append('dateTo', toDate.toISOString());
    if (warehouseId) params.append('warehouseId', warehouseId);

    const url = `${API_BASE_URL}/picking/completed?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch completed pick lists: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get complete details for a single completed pick list.
   * Includes header, order lines, and package information in a single request.
   *
   * @param pListId Pick list ID
   * @returns Complete pick list details or null if not found
   */
  async getCompletedPickListDetail(pListId: number): Promise<CompletedPickListDetailDto | null> {
    const url = `${API_BASE_URL}/picking/completed/${pListId}`;
    const response = await AuthService.authenticatedGet(url);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch completed pick list detail: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get order lines for a completed pick list.
   * Includes picking details from PListLine table.
   *
   * @param pListId Pick list ID
   * @returns List of order lines with picking details
   */
  async getCompletedPickListLines(pListId: number): Promise<CompletedPickListLineDto[]> {
    const url = `${API_BASE_URL}/picking/completed/${pListId}/lines`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch completed pick list lines: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Batch approve picking lists by order numbers
   * @param orderNumbers Array of order numbers to approve
   * @param targetStatus Optional target status (defaults to 10)
   * @param operatorId Optional operator ID
   * @returns Batch approval result with success/failure details
   */
  async batchApprovePListsByOrders(
    orderNumbers: number[],
    targetStatus?: number,
    operatorId?: string
  ): Promise<BatchApprovalResult> {
    const url = `${API_BASE_URL}/picking/lists/batch-approve`;
    const response = await AuthService.authenticatedPost(url, {
      orderNumbers,
      targetStatus,
      operatorId
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to batch approve pick lists'));
    }

    return response.json();
  }

  /**
   * Get packages for a batch picking list (PListIsBatchYesNo = true).
   * Used to display packages to operator before starting batch pick.
   * @param pListId The picking list ID
   * @returns Array of packages with packaging type description
   */
  async getPackagesForPList(pListId: number): Promise<PackageDto[]> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/packages`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch packages: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== Serial Number Registration Methods =====

  /**
   * Get serial numbers for a specific picking line
   */
  async getSerialNumbers(pListId: number, orderNo: number, lineNo: number): Promise<OrderSerialNoDto[]> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/serialnumbers`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch serial numbers: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Register a new serial number for a picking line
   */
  async createSerialNumber(pListId: number, orderNo: number, lineNo: number, serialNo: string): Promise<OrderSerialNoDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/serialnumbers`;
    const request: CreateOrderSerialNoRequest = {
      pListID: pListId,
      orderNo,
      lineNo,
      serialNo
    };

    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to create serial number'));
    }

    return response.json();
  }

  /**
   * Delete a serial number registration
   */
  async deleteSerialNumber(pListId: number, serialNoId: number): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/serialnumbers/${serialNoId}`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to delete serial number'));
    }
  }

  /**
   * Delete all serial numbers for a specific picking list line
   */
  async deleteSerialNumbersByLine(pListId: number, orderNo: number, lineNo: number): Promise<number> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/serialnumbers`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to delete serial numbers'));
    }

    const result = await response.json();
    return result.deletedCount || 0;
  }

  // ===== Batch Number Registration Methods =====

  /**
   * Get batch numbers for a specific picking line
   */
  async getBatchNumbers(pListId: number, orderNo: number, lineNo: number): Promise<OrderBatchNoDto[]> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/batchnumbers`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch batch numbers: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Register a new batch number for a picking line
   */
  async createBatchNumber(
    pListId: number,
    orderNo: number,
    lineNo: number,
    pListLineNo: number,
    batchNo: string,
    quantity: number,
    quantityGross?: number,
    expiryDate?: string | null
  ): Promise<OrderBatchNoDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/batchnumbers`;
    const request: CreateOrderBatchNoRequest = {
      pListID: pListId,
      orderNo,
      lineNo,
      pListLineNo,
      batchNo,
      quantity,
      quantityGross,
      expiryDate
    };

    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to create batch number'));
    }

    return response.json();
  }

  /**
   * Update a batch number registration
   */
  async updateBatchNumber(pListId: number, batchNoId: number, quantity: number, quantityGross?: number): Promise<OrderBatchNoDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/batchnumbers/${batchNoId}`;
    const request: UpdateOrderBatchNoRequest = {
      quantity,
      quantityGross
    };

    const response = await AuthService.authenticatedPut(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update batch number'));
    }

    return response.json();
  }

  /**
   * Delete a batch number registration
   */
  async deleteBatchNumber(pListId: number, batchNoId: number): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/batchnumbers/${batchNoId}`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to delete batch number'));
    }
  }

  // Additional Items (Tilleggsvarer) Methods

  async searchAdditionalItems(searchTerm: string): Promise<AdditionalItemDto[]> {
    const url = `${API_BASE_URL}/items/additional-items/search?searchTerm=${encodeURIComponent(searchTerm)}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to search additional items: ${response.statusText}`);
    }

    return response.json();
  }

  async addAdditionalLine(pListId: number, request: AddAdditionalLineRequest): Promise<AddAdditionalLineResponse> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/additional-lines`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to add additional line'));
    }

    return response.json();
  }

  /**
   * Slett en manuelt tillagt linje («Legg til vare») fra et aktivt plukk (TW 36391640).
   * Backend reverserer plukket antall, fjerner SSCC/serie/batch, sletter PListLine + OrderLine.
   */
  async deleteAdditionalLine(pListId: number, pListLineNo: number): Promise<{ success: boolean; message: string }> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/additional-lines/${pListLineNo}`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to delete additional line'));
    }

    return response.json();
  }

  /**
   * Sett nytt antall på en tilleggslinje (TW 36393701). Backend oppdaterer bestilt + plukket
   * antall via plukkflyten og justerer pallraden. Kun status < 50.
   */
  async setAdditionalLineQuantity(pListId: number, pListLineNo: number, quantity: number): Promise<{ success: boolean; message: string; quantity: number; qtyPicked: number }> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/additional-lines/${pListLineNo}`;
    const response = await AuthService.authenticatedPut(url, { quantity });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update additional line quantity'));
    }

    return response.json();
  }

  /**
   * Lagersjef «Endre antall» på en plukklistelinje — kun Compass GO (TW 36409951).
   * Øker eller reduserer bestilt antall (plukklinje + ordrelinje), også på låste lister.
   * Avvisning (409) bærer en norsk melding som kastes som Error.
   */
  /**
   * «Memo» på plukklistelinjer (Haucon 2026-10-07): infofeltene plukkerne har satt opp i profilen.
   * Styrer om «Memo»-knappen vises (hasEditableFields).
   */
  async getLineMemoConfig(): Promise<PListLineMemoConfig> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/picking/line-memo/config`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to load line memo config'));
    }
    return response.json();
  }

  /** Infofeltene med nåverdi for én plukklistelinje (leses fra ordrelinjen). */
  async getLineMemo(pListId: number, pListLineNo: number): Promise<PListLineMemo> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/picking/lists/${pListId}/lines/${pListLineNo}/memo`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to load line memo'));
    }
    return response.json();
  }

  /** Lagrer redigerbare infofelt på ordrelinjen. Tom tekst fjerner verdien. */
  async updateLineMemo(pListId: number, pListLineNo: number, values: Record<string, string | null>): Promise<{ success: boolean; message: string; memo?: PListLineMemo }> {
    const response = await AuthService.authenticatedPut(`${API_BASE_URL}/picking/lists/${pListId}/lines/${pListLineNo}/memo`, { values });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to save line memo'));
    }
    return response.json();
  }

  async changeLineQuantity(pListId: number, pListLineNo: number, quantity: number): Promise<{ success: boolean; message: string; oldQuantity: number; quantity: number; qtyPicked: number; lineStatus: number }> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${pListLineNo}/quantity`;
    const response = await AuthService.authenticatedPut(url, { quantity });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to change line quantity'));
    }

    return response.json();
  }

  /**
   * Restordre ved underplukk (TW 36411479): lagrer «Rest / Ikke rest» per avvikslinje fra
   * avviksdialogen på ordrelinjen (ChangeQuantityOnDeviation) før avslutning.
   */
  async setRemainderChoices(pListId: number, choices: Array<{ pListLineNo: number; createRemainder: boolean }>): Promise<{ success: boolean; message: string; updated: number }> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/remainder-choices`;
    const response = await AuthService.authenticatedPut(url, { choices });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to save remainder choices'));
    }

    return response.json();
  }

  /**
   * Lagersjef «Slett linje» på en plukklistelinje — kun Compass GO (TW 36411059).
   * Sletter plukklinjen og ordrelinjen. Avvisning (409) bærer en norsk melding som kastes som Error.
   */
  async deleteLine(pListId: number, pListLineNo: number): Promise<{ success: boolean; message: string }> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${pListLineNo}`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to delete line'));
    }

    return response.json();
  }
  // ==================== Aggregated Items ====================

  async getAggregatedItems(warehouseId?: string): Promise<AggregatedPickingItemDto[]> {
    const params = new URLSearchParams();
    if (warehouseId) params.append('warehouseId', warehouseId);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const url = `${API_BASE_URL}/picking/aggregated-items${queryString}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch aggregated items: ${response.statusText}`);
    }

    return response.json();
  }

  // ==================== SSCC Validation ====================

  async validateSSCCForCompletion(pListId: number): Promise<SSCCValidationResult> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/validate-sscc`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to validate SSCC: ${response.statusText}`);
    }

    return response.json();
  }

  // Operator Batch Picking

  async validateOperatorBatch(pListIds: number[]): Promise<ValidateOperatorBatchResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/picking/operator-batch/validate`,
      { pListIds }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Validering av samleplukk feilet'));
    }
    return response.json();
  }

  async createOperatorBatch(pListIds: number[]): Promise<CreateOperatorBatchResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/picking/operator-batch/create`,
      { pListIds }
    );
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(extractApiError(data, response, 'Opprettelse av samleplukk feilet'));
    }
    return response.json();
  }
}

// Package types for batch picking
export interface PackageDto {
  packageNo: number;
  visiblePackageNo?: number;
  packagingTypeNo: number;
  packagingTypeDescription?: string;
  pListID: number;
  description1?: string;
  description2?: string;
  carrierName?: string;
  weight?: number;
  volume?: number;
  /** Ordren pakken tilhører (laveste når esken deles) — kontekst for kollietiketten */
  orderNo?: number | null;
  /** Alle ordrer esken tilhører — flere når ordrer deler eske i samplukk (TW 36438546) */
  orderNos?: number[];
}

// Batch approval types
export interface BatchApprovalRequest {
  orderNumbers: number[];
  targetStatus?: number;
  operatorId?: string;
}

export interface BatchApprovalResult {
  totalProcessed: number;
  successCount: number;
  failedCount: number;
  notFoundCount: number;
  skippedCount: number;
  results: BatchApprovalItemResult[];
  errors: string[];
}

export interface BatchApprovalItemResult {
  pListID: number;
  orderNo: number;
  success: boolean;
  errorMessage?: string;
  oldStatus?: number;
  newStatus?: number;
}

export const pickingService = new PickingService();
